import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import setInitialValues from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetInitialValues";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetIsPreloadingData";
import updateCart from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/UpdateCart";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Page from "@insite/mobius/Page";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { Component, createContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    preloadCheckoutShippingData: makeHandlerChainAwaitable(preloadCheckoutShippingData),
    setIsPreloadingData,
    updateCart: makeHandlerChainAwaitable(updateCart),
    setInitialValues,
};

const mapStateToProps = (state: ApplicationState) => {
    const cartId = state.pages.checkoutReviewAndSubmit.cartId;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const { isUpdatingCart } = state.pages.checkoutShipping;
    const billTo = getBillToState(state, cartState.value?.billToId).value;
    const addressFieldsDataView = getAddressFieldsDataView(state);

    return {
        cartId,
        isPreloadingData: state.pages.checkoutShipping.isPreloadingData,
        reviewAndSubmitPageLink: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage"),
        location: getLocation(state),
        isLoading: !cartState.value || cartState.isLoading || isUpdatingCart,
        session: getSession(state),
        useOneTimeAddress: state.pages.checkoutShipping.useOneTimeAddress,
        billTo,
        addressFieldsDataView,
    };
};
export interface CheckoutReviewShippingStyles {
    buttonWrapper?: InjectableCss;
    continueButton?: ButtonPresentationProps;
}

export const styles: CheckoutReviewShippingStyles = {
    continueButton: {
        variant: "primary",
        css: css`
            display: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 100%;
                    `,
                    css`
                        width: 50%;
                    `,
                    css`
                        width: 50%;
                    `,
                ])}
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 2rem;
        `,
    },
};

type Props = PageProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps> & HasHistory;

type Validator = () => Promise<boolean>;

type State = {
    formSubmitAttempted: boolean;
    validators: {
        [key: string]: Validator | undefined;
    };
    isRedirecting: boolean;
};

export const CheckoutShippingFormContext = createContext<State>({
    formSubmitAttempted: false,
    validators: {},
    isRedirecting: false,
});

class CheckoutReviewShipping extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            formSubmitAttempted: false,
            validators: {},
            isRedirecting: false,
        };
    }

    async componentDidMount() {
        if (!this.props.isPreloadingData) {
            const { cartId } = parseQueryString<{ cartId?: string }>(this.props.location.search);
            await this.props.preloadCheckoutShippingData({ cartId: cartId?.toLowerCase() });
        }
        this.props.setIsPreloadingData({ isPreloadingData: false });
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        if (prevProps.billTo?.validation !== this.props.billTo?.validation) {
            if (
                prevProps.addressFieldsDataView.value?.billToAddressFields !==
                this.props.addressFieldsDataView.value?.billToAddressFields
            ) {
                this.props.setInitialValues();
            }
        }
    }

    scrollToFirstFormError = () => {
        const firstErrorElement = document.querySelector("[aria-invalid='true']");
        if (firstErrorElement) {
            const formFieldParent = firstErrorElement.closest("[class*='FormFieldStyle']");
            if (!formFieldParent) {
                return;
            }
            const { top } = formFieldParent.getBoundingClientRect();
            window.scrollTo({
                top: top + window.pageYOffset,
                left: 0,
                behavior: "smooth",
            });
        }
    };

    handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        this.setState({ formSubmitAttempted: true });

        const validators: Validator[] = [];
        Object.keys(this.state.validators).forEach(key => {
            const validator = this.state.validators[key];
            if (validator) {
                validators.push(validator);
            }
        });
        const results = await Promise.all(validators.map(v => v()));

        if (results.every(o => o)) {
            this.setState({ isRedirecting: true });
            await this.props.updateCart({});
            const continueUrl = this.props.cartId
                ? `${this.props.reviewAndSubmitPageLink!.url}?cartId=${this.props.cartId}`
                : this.props.reviewAndSubmitPageLink!.url;
            this.props.history.push(continueUrl);
        } else {
            this.scrollToFirstFormError();
        }
    };

    render() {
        const { session } = this.props;

        return (
            <Page
                data-test-selector="checkoutShipping"
                css={css`
                    padding-bottom: 0 !important;
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(
                            theme,
                            [
                                null,
                                null,
                                css `
                                    max-width:100%;                                    
                                `,
                                css`
                                    overflow: visible !important;                                    
                                `,
                                css`
                                    overflow: visible !important;
                                `,
                            ],
                            "max",
                        )}
                `}
            >
                <CheckoutShippingFormContext.Provider value={this.state}>
                    <form id="checkoutShippingForm" onSubmit={this.handleFormSubmit} noValidate>
                        <Zone zoneName="Content" contentId={this.props.id} />
                        <StyledWrapper {...styles.buttonWrapper}>
                            {(session.isGuest || this.props.useOneTimeAddress) && (
                                <Button
                                    {...styles.continueButton}
                                    type="submit"
                                    disabled={this.props.isLoading}
                                    data-test-selector="checkoutShippingCartTotalContinueButton"
                                    id="checkoutShippingAdressForm-Submit"
                                >
                                    {translate("Save & Continue")}
                                </Button>
                            )}
                        </StyledWrapper>
                    </form>
                </CheckoutShippingFormContext.Provider>
            </Page>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(CheckoutReviewShipping)),
    definition: {
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
        displayName: "Checkout Review Shipping",
    },
};

export default widgetModule;
